import React, { useState, useEffect, useRef, useCallback } from "react";
import { handleTokenAndPostRequest } from "../utils/utils";
import GradientHouseIcon from "./GradientHouseIcon";
import VendorleadsGradientText from "./VendorleadsGradientText";
import {
  FaUser, FaPalette, FaMapMarkerAlt, FaCloudUploadAlt,
  FaTimes, FaCheckCircle, FaExclamationTriangle, FaFileAlt,
  FaArrowRight, FaArrowLeft, FaCamera, FaSearch,
  FaExternalLinkAlt, FaEnvelope, FaCheck, FaGlobe,
  FaPen, FaLink, FaPlug,
} from "react-icons/fa";
import ConnectCRMModal from "./ConnectCRMModal";

const STEPS = [
  { key: "profile", label: "Profile", icon: <FaUser size={14} /> },
  { key: "branding", label: "Branding", icon: <FaPalette size={14} /> },
  { key: "suburbs", label: "Suburbs", icon: <FaMapMarkerAlt size={14} /> },
  { key: "contacts", label: "Contacts", icon: <FaCloudUploadAlt size={14} /> },
];

const WIZARD_CSS = `
@keyframes wizardSpin { to { transform: rotate(360deg); } }
@keyframes wizardFadeIn { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
`;

function InlineSpinner({ size = 16, color = "#fff" }) {
  return (
    <span style={{
      display: "inline-block", width: size, height: size,
      border: `2px solid rgba(255,255,255,0.25)`, borderTopColor: color,
      borderRadius: "50%", animation: "wizardSpin 0.7s linear infinite",
    }} />
  );
}

// Sanitize possibly NaN/null default values from backend
function cleanStr(val) {
  if (!val || val === "nan" || val === ".nan" || val === "None" || val === "null") return "";
  return String(val).trim();
}

export default function OnboardingWizard({ agentDetails, onComplete }) {
  const [step, setStep] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Step 1: Profile
  const [agentName, setAgentName] = useState(cleanStr(agentDetails?.agent_name));
  const [agentFirstName, setAgentFirstName] = useState(cleanStr(agentDetails?.agent_first_name));
  const [agentTitle, setAgentTitle] = useState(cleanStr(agentDetails?.agent_title));
  const [profileImage, setProfileImage] = useState(cleanStr(agentDetails?.agent_profile_image));
  const [uploadingProfile, setUploadingProfile] = useState(false);
  const profileInputRef = useRef();

  // Step 2: Branding
  const [agencyName, setAgencyName] = useState(cleanStr(agentDetails?.agency_name_long) || cleanStr(agentDetails?.agency_name));
  const [agencyNameShort, setAgencyNameShort] = useState(cleanStr(agentDetails?.agency_name_short));
  const [logoUrl, setLogoUrl] = useState(cleanStr(agentDetails?.logo_url));
  const [agentColour, setAgentColour] = useState(cleanStr(agentDetails?.agent_colour) || "#0f766e");
  const [buttonColour, setButtonColour] = useState(cleanStr(agentDetails?.agent_button_colour) || "#10b981");
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef();
  const [showDomainImport, setShowDomainImport] = useState(false);
  const [domainUrl, setDomainUrl] = useState("");
  const [domainLoading, setDomainLoading] = useState(false);
  const [domainMsg, setDomainMsg] = useState("");

  // Step 3: Suburbs — primary suburb (single-select) + target suburbs (multi-select)
  const [primarySuburb, setPrimarySuburb] = useState(null);
  const [primaryQuery, setPrimaryQuery] = useState("");
  const [primaryResults, setPrimaryResults] = useState([]);
  const [primarySearching, setPrimarySearching] = useState(false);
  const [primaryDropdownOpen, setPrimaryDropdownOpen] = useState(false);
  const primarySearchTimer = useRef(null);
  const primaryInputRef = useRef();
  const primaryDropdownRef = useRef(null);

  const [targetSuburbs, setTargetSuburbs] = useState([]);
  const [suburbQuery, setSuburbQuery] = useState("");
  const [suburbResults, setSuburbResults] = useState([]);
  const [suburbSearching, setSuburbSearching] = useState(false);
  const [suburbDropdownOpen, setSuburbDropdownOpen] = useState(false);
  const suburbSearchTimer = useRef(null);
  const suburbInputRef = useRef();
  const suburbDropdownRef = useRef(null);

  // Step 4: Contacts
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [progress, setProgress] = useState(0);
  const progressRef = useRef(null);
  const fileInputRef = useRef();
  const [crmName, setCrmName] = useState("");
  const [crmSent, setCrmSent] = useState(false);
  const [crmSending, setCrmSending] = useState(false);
  const [showCRMModal, setShowCRMModal] = useState(false);

  // General
  const [saving, setSaving] = useState(false);
  const [stepComplete, setStepComplete] = useState({});

  const slug = agentDetails?.landing_page_slug || "";

  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);

  // Load existing target suburbs + primary suburb
  useEffect(() => {
    const savedIds = Array.isArray(agentDetails?.target_suburb_ids) ? agentDetails.target_suburb_ids : [];
    setTargetSuburbs(savedIds.map(id => ({
      value: id, type: "suburb",
      label: id.replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase()).replace(/ Nsw | Vic | Qld | Sa | Wa | Tas | Nt | Act /i, m => " " + m.trim().toUpperCase() + " "),
      state: (id.match(/-([a-z]{2,3})-\d+$/)?.[1] || "").toUpperCase(),
    })));
    const sub = cleanStr(agentDetails?.default_market_update_suburb);
    const st = cleanStr(agentDetails?.default_market_update_state);
    const pc = cleanStr(agentDetails?.default_market_update_postcode);
    if (sub) {
      setPrimarySuburb({
        value: `${sub.toLowerCase().replace(/\s+/g, "-")}-${(st || "").toLowerCase()}-${pc}`,
        label: `${sub}${st ? ", " + st : ""}${pc ? " " + pc : ""}`,
        state: st,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close dropdowns on outside click
  useEffect(() => {
    function handleClickOutside(e) {
      if (suburbDropdownRef.current && !suburbDropdownRef.current.contains(e.target)) setSuburbDropdownOpen(false);
      if (primaryDropdownRef.current && !primaryDropdownRef.current.contains(e.target)) setPrimaryDropdownOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Upload progress bar
  useEffect(() => {
    if (uploading) {
      setProgress(0);
      const duration = 10000, interval = 100, stepSize = 100 / (duration / interval);
      progressRef.current = setInterval(() => {
        setProgress(prev => { const next = prev + stepSize; if (next >= 100) { clearInterval(progressRef.current); return 100; } return next; });
      }, interval);
    } else {
      clearInterval(progressRef.current);
      if (uploadResult?.success) setProgress(100);
    }
    return () => clearInterval(progressRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploading]);

  // Generic suburb search helper
  const doSuburbSearch = useCallback((query, timerRef, setResults, setOpen, setSearching, exclude) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (!query.trim()) { setResults([]); setOpen(false); return; }
    timerRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await fetch(`${window.api_host}/location-search?q=${encodeURIComponent(query)}`);
        const data = await res.json();
        const filtered = (Array.isArray(data) ? data : []).filter(
          item => item.type !== "postcode" && !exclude.some(s => s.value === item.value)
        );
        setResults(filtered);
        setOpen(filtered.length > 0);
      } catch { setResults([]); }
      setSearching(false);
    }, 300);
  }, []);

  const searchPrimary = useCallback((q) => doSuburbSearch(q, primarySearchTimer, setPrimaryResults, setPrimaryDropdownOpen, setPrimarySearching, []),
    [doSuburbSearch]);
  const searchSuburbs = useCallback((q) => doSuburbSearch(q, suburbSearchTimer, setSuburbResults, setSuburbDropdownOpen, setSuburbSearching, targetSuburbs),
    [doSuburbSearch, targetSuburbs]);

  const handleSelectPrimary = (item) => {
    setPrimarySuburb(item);
    setPrimaryQuery("");
    setPrimaryResults([]);
    setPrimaryDropdownOpen(false);
  };

  const handleSelectSuburb = (item) => {
    if (targetSuburbs.some(s => s.value === item.value)) return;
    setTargetSuburbs(prev => [...prev, item]);
    setSuburbQuery("");
    const remaining = suburbResults.filter(r => r.value !== item.value);
    setSuburbResults(remaining);
    if (remaining.length === 0) setSuburbDropdownOpen(false);
    if (suburbInputRef.current) suburbInputRef.current.focus();
  };

  const handleRemoveSuburb = (idx) => {
    setTargetSuburbs(prev => prev.filter((_, i) => i !== idx));
  };

  // Image upload
  const handleImageUpload = async (field, uploadFile, setUrl, setUpl) => {
    if (!uploadFile) return;
    setUpl(true);
    try {
      const formData = new FormData();
      formData.append("file", uploadFile);
      formData.append("field", field);
      const res = await handleTokenAndPostRequest(`${window.api_host}/update-agent-image`, formData);
      if (res.data.success && res.data.url) setUrl(res.data.url);
    } catch {}
    setUpl(false);
  };

  // Domain import
  const handleDomainImport = async () => {
    if (!domainUrl.trim()) return;
    setDomainLoading(true); setDomainMsg("");
    try {
      const res = await handleTokenAndPostRequest(
        `${window.api_host}/scrape-domain-profile`,
        { profile_url: domainUrl.trim() },
        { "Content-Type": "application/json" }
      );
      if (res.data.success && res.data.data) {
        const d = res.data.data;
        if (d.agent_name) setAgentName(d.agent_name);
        if (d.agent_first_name) setAgentFirstName(d.agent_first_name);
        if (d.agent_title) setAgentTitle(d.agent_title);
        if (d.agent_profile_image) setProfileImage(d.agent_profile_image);
        if (d.agency_name) setAgencyName(d.agency_name);
        if (d.agency_name_short) setAgencyNameShort(d.agency_name_short);
        if (d.logo_url) setLogoUrl(d.logo_url);
        if (d.agent_colour) setAgentColour(d.agent_colour);
        if (d.agent_button_colour) setButtonColour(d.agent_button_colour);
        setDomainMsg("Branding imported successfully!");
      } else {
        setDomainMsg(res.data.message || "Failed to import.");
      }
    } catch { setDomainMsg("Failed to import. Please check the URL."); }
    setDomainLoading(false);
  };

  // Save step
  const saveStep = async (nextStep) => {
    setSaving(true);
    try {
      let updates = {};
      if (step === 0) {
        updates = { agent_name: agentName, agent_first_name: agentFirstName, agent_title: agentTitle };
      } else if (step === 1) {
        updates = {
          agency_name_long: agencyName, agency_name_short: agencyNameShort,
          agent_colour: agentColour, agent_button_colour: buttonColour,
        };
      } else if (step === 2) {
        const pcMatch = primarySuburb?.value?.match(/-(\d{4})$/);
        updates = {
          default_market_update_suburb: primarySuburb?.label?.split(",")[0]?.trim() || "",
          default_market_update_postcode: pcMatch ? pcMatch[1] : "",
          default_market_update_state: primarySuburb?.state || "",
          target_suburb_ids: targetSuburbs.map(s => s.value),
        };
      }
      if (Object.keys(updates).length > 0) {
        await handleTokenAndPostRequest(`${window.api_host}/update-agent-details`, updates, { "Content-Type": "application/json" });
        try {
          const user = JSON.parse(localStorage.getItem("user") || "{}");
          user.agent_details = { ...user.agent_details, ...updates };
          if (updates.agent_name) user.name = updates.agent_name;
          localStorage.setItem("user", JSON.stringify(user));
        } catch {}
      }
      setStepComplete(prev => ({ ...prev, [step]: true }));
      if (nextStep !== undefined) setStep(nextStep);
    } catch {}
    setSaving(false);
  };

  // File handling
  const handleFile = (f) => {
    const ext = f.name.substring(f.name.lastIndexOf(".")).toLowerCase();
    if (![".csv", ".xlsx", ".xls"].includes(ext)) {
      setUploadResult({ success: false, message: "Please upload a CSV or Excel file." });
      return;
    }
    setFile(f); setUploadResult(null);
  };
  const handleDrop = (e) => { e.preventDefault(); setDragOver(false); if (e.dataTransfer.files?.[0]) handleFile(e.dataTransfer.files[0]); };

  const handleUploadContacts = async () => {
    if (!file) return;
    setUploading(true); setUploadResult(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await handleTokenAndPostRequest(`${window.api_host}/upload-contacts`, formData);
      setUploadResult({ success: true, message: res.data?.message || "Uploaded! Contacts will be processed within 24 hours." });
      setFile(null);
      window.dispatchEvent(new Event("contacts-uploaded"));
    } catch (err) {
      setUploadResult({ success: false, message: err?.response?.data?.error || "Upload failed." });
    } finally { setUploading(false); }
  };

  const handleRequestCrm = async () => {
    if (!crmName.trim()) return;
    setCrmSending(true);
    try {
      await handleTokenAndPostRequest(`${window.api_host}/request-crm-link`, { crm_name: crmName.trim() }, { "Content-Type": "application/json" });
      setCrmSent(true);
    } catch {}
    setCrmSending(false);
  };

  const handleFinish = () => { sessionStorage.setItem("onboarding_wizard_done", "1"); onComplete(); };

  // Compute auto font colour from brand colour
  const autoFontColour = (() => {
    try {
      const hex = agentColour.replace("#", "");
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      return (r * 0.299 + g * 0.587 + b * 0.114) > 150 ? "#1a1a1a" : "#ffffff";
    } catch { return "#ffffff"; }
  })();

  const autoButtonFontColour = (() => {
    try {
      const hex = buttonColour.replace("#", "");
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      return (r * 0.299 + g * 0.587 + b * 0.114) > 150 ? "#1a1a1a" : "#ffffff";
    } catch { return "#ffffff"; }
  })();

  // ===================== STYLES =====================
  const card = {
    background: "#fff",
    borderRadius: 20,
    border: "1px solid rgba(0,0,0,0.06)",
    boxShadow: "0 1px 3px rgba(0,0,0,0.04), 0 4px 20px rgba(0,0,0,0.03)",
    padding: isMobile ? "24px 20px" : "32px 36px",
    marginBottom: 20,
    animation: "wizardFadeIn 0.35s ease-out",
  };

  const labelStyle = { display: "block", fontSize: 12, fontWeight: 600, color: "#64748b", marginBottom: 6, letterSpacing: "0.03em", textTransform: "uppercase" };

  const inputStyle = {
    width: "100%", padding: "12px 16px", fontSize: 15,
    border: "1.5px solid #e2e8f0", borderRadius: 12, outline: "none",
    background: "#fafbfc", color: "#0f172a", boxSizing: "border-box",
    transition: "border-color 0.2s, box-shadow 0.2s",
  };

  const btnPrimary = {
    padding: "13px 32px", borderRadius: 12, border: "none",
    background: "linear-gradient(135deg, #0f766e 0%, #10b981 100%)",
    color: "#fff", fontSize: 15, fontWeight: 600, cursor: "pointer",
    display: "inline-flex", alignItems: "center", gap: 8,
    boxShadow: "0 2px 8px rgba(16,185,129,0.25)",
    transition: "transform 0.15s, box-shadow 0.15s",
  };

  const btnSecondary = {
    padding: "13px 24px", borderRadius: 12,
    border: "1.5px solid #e2e8f0", background: "#fff",
    color: "#475569", fontSize: 15, fontWeight: 500, cursor: "pointer",
    display: "inline-flex", alignItems: "center", gap: 8,
    transition: "border-color 0.2s",
  };

  const btnGhost = {
    padding: "10px 18px", borderRadius: 10, border: "1.5px dashed #cbd5e1",
    background: "transparent", color: "#64748b", fontSize: 13, fontWeight: 500,
    cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6,
    transition: "border-color 0.2s, color 0.2s",
  };

  // ===================== STEP INDICATOR =====================
  const renderStepIndicator = () => (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "center",
      gap: 0, marginBottom: 36,
    }}>
      {STEPS.map((s, i) => {
        const isActive = i === step;
        const isDone = stepComplete[i] || i < step;
        return (
          <React.Fragment key={s.key}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6, minWidth: isMobile ? 48 : 72 }}>
              <div style={{
                width: 40, height: 40,
                borderRadius: "50%",
                background: isActive
                  ? "linear-gradient(135deg, #0f766e, #10b981)"
                  : isDone ? "#d1fae5" : "#f1f5f9",
                color: isActive ? "#fff" : isDone ? "#059669" : "#94a3b8",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 15, fontWeight: 600,
                transition: "all 0.3s ease",
                boxShadow: isActive ? "0 4px 14px rgba(16,185,129,0.3)" : "none",
              }}>
                {isDone && !isActive ? <FaCheck size={14} /> : s.icon}
              </div>
              <span style={{
                fontSize: 11, fontWeight: isActive ? 600 : 500,
                color: isActive ? "#0f766e" : isDone ? "#059669" : "#94a3b8",
                letterSpacing: "0.02em",
                textAlign: "center",
                whiteSpace: "nowrap",
              }}>
                {s.label}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div style={{
                width: isMobile ? 24 : 48, height: 2,
                background: isDone ? "linear-gradient(90deg, #10b981, #0f766e)" : "#e2e8f0",
                borderRadius: 1,
                marginBottom: 22,
                transition: "background 0.3s",
              }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );

  // ===================== STEP 0: PROFILE =====================
  const renderStep0 = () => (
    <div style={card}>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 22, fontWeight: 700, color: "#0f172a", margin: 0, letterSpacing: "-0.01em" }}>
          Confirm Your Details
        </h2>
        <p style={{ fontSize: 14, color: "#64748b", margin: "6px 0 0", lineHeight: 1.6 }}>
          These appear on your property reports, emails, and landing page.
        </p>
      </div>

      {/* Profile image */}
      <div style={{ display: "flex", alignItems: "center", gap: 18, marginBottom: 28, padding: "16px 20px", background: "#f8fafc", borderRadius: 16 }}>
        <div
          onClick={() => !uploadingProfile && profileInputRef.current?.click()}
          style={{ position: "relative", flexShrink: 0, cursor: "pointer" }}
        >
          <div style={{
            width: 80, height: 80, borderRadius: "50%", overflow: "hidden",
            background: "#e2e8f0", border: "3px solid #fff",
            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
            display: "flex", alignItems: "center", justifyContent: "center",
            position: "relative",
          }}>
            {profileImage ? (
              <img src={profileImage} alt="Profile" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            ) : (
              <FaUser size={28} color="#94a3b8" />
            )}
            {/* Hover overlay */}
            <div
              className="photo-overlay"
              style={{
                position: "absolute", inset: 0, borderRadius: "50%",
                background: "rgba(0,0,0,0.45)",
                display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                opacity: 0, transition: "opacity 0.2s",
                color: "#fff", gap: 2,
              }}
              onMouseEnter={e => e.currentTarget.style.opacity = 1}
              onMouseLeave={e => e.currentTarget.style.opacity = 0}
            >
              {uploadingProfile ? <InlineSpinner size={14} /> : <><FaCamera size={14} /><span style={{ fontSize: 9, fontWeight: 600, letterSpacing: "0.03em" }}>EDIT</span></>}
            </div>
          </div>
          <div style={{
            position: "absolute", bottom: 0, right: 0,
            width: 28, height: 28, borderRadius: "50%",
            background: "linear-gradient(135deg, #0f766e, #10b981)",
            border: "2.5px solid #fff", color: "#fff",
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 2px 6px rgba(16,185,129,0.3)",
            pointerEvents: "none",
          }}>
            <FaPen size={10} />
          </div>
          <input
            ref={profileInputRef} type="file" accept="image/*" style={{ display: "none" }}
            onChange={e => { if (e.target.files[0]) handleImageUpload("agent_profile_image", e.target.files[0], setProfileImage, setUploadingProfile); }}
          />
        </div>
        <div>
          <div style={{ fontSize: 17, fontWeight: 600, color: "#0f172a" }}>{agentName || "Your Name"}</div>
          <div style={{ fontSize: 13, color: "#64748b", marginTop: 2 }}>{agentTitle || "Your Title"}</div>
          <div
            onClick={() => !uploadingProfile && profileInputRef.current?.click()}
            style={{ fontSize: 12, color: "#0f766e", fontWeight: 600, marginTop: 6, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }}
          >
            <FaCamera size={10} /> {profileImage ? "Change Photo" : "Upload Photo"}
          </div>
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
        <div>
          <label style={labelStyle}>Full Name</label>
          <input style={inputStyle} value={agentName} onChange={e => setAgentName(e.target.value)} placeholder="e.g. Sarah Mitchell" />
        </div>
        <div>
          <label style={labelStyle}>First Name</label>
          <input style={inputStyle} value={agentFirstName} onChange={e => setAgentFirstName(e.target.value)} placeholder="e.g. Sarah" />
        </div>
        <div>
          <label style={labelStyle}>Job Title</label>
          <input style={inputStyle} value={agentTitle} onChange={e => setAgentTitle(e.target.value)} placeholder="e.g. Licensed Real Estate Agent" />
        </div>
      </div>
    </div>
  );

  // ===================== STEP 1: BRANDING =====================
  const renderStep1 = () => (
    <div>
      <div style={card}>
        <div style={{ marginBottom: 24 }}>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: "#0f172a", margin: 0, letterSpacing: "-0.01em" }}>
            Agency Branding
          </h2>
          <p style={{ fontSize: 14, color: "#64748b", margin: "6px 0 0", lineHeight: 1.6 }}>
            Your branding appears on property reports, email templates, and your landing page.
          </p>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          <div>
            <label style={labelStyle}>Agency Name (Full)</label>
            <input style={inputStyle} value={agencyName} onChange={e => setAgencyName(e.target.value)} placeholder="e.g. Ray White Bondi Beach" />
          </div>
          <div>
            <label style={labelStyle}>Agency Name (Short)</label>
            <input style={inputStyle} value={agencyNameShort} onChange={e => setAgencyNameShort(e.target.value)} placeholder="e.g. Ray White" />
          </div>

          {/* Logo */}
          <div>
            <label style={labelStyle}>Agency Logo</label>
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <div style={{
                width: 56, height: 56, borderRadius: 14, overflow: "hidden",
                background: "#f1f5f9", border: "1.5px solid #e2e8f0",
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0,
              }}>
                {logoUrl ? (
                  <img src={logoUrl} alt="Logo" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
                ) : (
                  <FaPalette size={18} color="#94a3b8" />
                )}
              </div>
              <button
                onClick={() => logoInputRef.current?.click()}
                disabled={uploadingLogo}
                style={{ ...btnGhost, gap: 6 }}
              >
                {uploadingLogo ? <InlineSpinner size={12} color="#64748b" /> : <FaCamera size={12} />}
                {logoUrl ? "Change Logo" : "Upload Logo"}
              </button>
              <input
                ref={logoInputRef} type="file" accept="image/*" style={{ display: "none" }}
                onChange={e => { if (e.target.files[0]) handleImageUpload("logo_url", e.target.files[0], setLogoUrl, setUploadingLogo); }}
              />
            </div>
          </div>

          {/* Colours */}
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
            {[
              { l: "Brand Colour", v: agentColour, s: setAgentColour, id: "brand-colour" },
              { l: "Button Colour", v: buttonColour, s: setButtonColour, id: "button-colour" },
            ].map(c => (
              <div key={c.l} style={{ flex: "1 1 200px", minWidth: 180 }}>
                <label style={labelStyle}>{c.l}</label>
                <div
                  onClick={() => document.getElementById(c.id)?.click()}
                  style={{
                    display: "flex", alignItems: "center", gap: 0,
                    border: "1.5px solid #e2e8f0", borderRadius: 12, overflow: "hidden",
                    cursor: "pointer", background: "#fafbfc",
                    transition: "border-color 0.2s",
                  }}
                >
                  {/* Colour swatch */}
                  <div style={{
                    width: 48, minHeight: 44, background: c.v,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    borderRight: "1.5px solid #e2e8f0", flexShrink: 0,
                    position: "relative",
                  }}>
                    <FaPen size={10} color={(() => {
                      try {
                        const h = c.v.replace("#", "");
                        const r = parseInt(h.substring(0, 2), 16);
                        const g = parseInt(h.substring(2, 4), 16);
                        const b = parseInt(h.substring(4, 6), 16);
                        return (r * 0.299 + g * 0.587 + b * 0.114) > 150 ? "rgba(0,0,0,0.45)" : "rgba(255,255,255,0.7)";
                      } catch { return "rgba(255,255,255,0.7)"; }
                    })()} />
                    <input
                      id={c.id}
                      type="color" value={c.v} onChange={e => { e.stopPropagation(); c.s(e.target.value); }}
                      style={{
                        position: "absolute", inset: 0, opacity: 0,
                        width: "100%", height: "100%",
                        cursor: "pointer", border: "none",
                      }}
                    />
                  </div>
                  {/* Hex input */}
                  <input
                    style={{
                      flex: 1, border: "none", outline: "none", background: "transparent",
                      fontFamily: "'SF Mono', Consolas, monospace", fontSize: 13,
                      padding: "11px 12px", color: "#0f172a", minWidth: 0,
                    }}
                    value={c.v}
                    onClick={e => e.stopPropagation()}
                    onChange={e => c.s(e.target.value)}
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Preview — browser mockup */}
          <div style={{ marginTop: 4 }}>
            <label style={labelStyle}>Website Preview</label>
            <div style={{ borderRadius: 14, overflow: "hidden", border: "1.5px solid #e2e8f0", boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
              {/* Browser chrome */}
              <div style={{
                background: "#f1f5f9", padding: "8px 14px",
                display: "flex", alignItems: "center", gap: 8,
                borderBottom: "1px solid #e2e8f0",
              }}>
                <div style={{ display: "flex", gap: 5 }}>
                  <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#fca5a5" }} />
                  <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#fde68a" }} />
                  <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#86efac" }} />
                </div>
                <div style={{
                  flex: 1, background: "#fff", borderRadius: 6, padding: "4px 10px",
                  fontSize: 11, color: "#94a3b8", fontFamily: "'SF Mono', Consolas, monospace",
                  border: "1px solid #e2e8f0", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>
                  homeprice.site/property-search-live
                </div>
              </div>
              {/* Nav bar */}
              <div style={{
                background: agentColour, padding: "12px 20px",
                display: "flex", alignItems: "center", gap: 12,
              }}>
                {logoUrl && (
                  <img src={logoUrl} alt="Logo" style={{ height: 24, maxWidth: 70, objectFit: "contain", borderRadius: 3 }} />
                )}
                <span style={{ color: autoFontColour, fontSize: 14, fontWeight: 600 }}>
                  {agencyNameShort || agencyName || "Your Agency"}
                </span>
              </div>
              {/* Page body */}
              <div style={{ padding: "20px 20px 24px", background: "#fff" }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: "#0f172a", marginBottom: 4 }}>What's your home worth?</div>
                <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 14 }}>Get a free AI-powered property appraisal</div>
                {/* Fake search bar */}
                <div style={{
                  display: "flex", alignItems: "center", gap: 8,
                  border: "1.5px solid #e2e8f0", borderRadius: 10, padding: "10px 14px",
                  marginBottom: 14, background: "#fafbfc",
                }}>
                  <FaSearch size={11} color="#cbd5e1" />
                  <span style={{ fontSize: 12, color: "#cbd5e1" }}>Enter your address...</span>
                </div>
                <div style={{
                  background: buttonColour, color: autoButtonFontColour,
                  borderRadius: 8, padding: "9px 18px",
                  fontSize: 12, fontWeight: 600, display: "inline-block",
                  pointerEvents: "none",
                }}>
                  Get Your Free Appraisal
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Domain import toggle */}
        <div style={{ marginTop: 24, borderTop: "1px solid #f1f5f9", paddingTop: 20 }}>
          {!showDomainImport ? (
            <button onClick={() => setShowDomainImport(true)} style={btnGhost}>
              <FaGlobe size={13} /> Import from Domain.com.au
            </button>
          ) : (
            <div style={{ animation: "wizardFadeIn 0.25s ease-out" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                <FaGlobe size={13} color="#0f766e" />
                <span style={{ fontSize: 13, fontWeight: 600, color: "#0f766e" }}>Import from Domain.com.au</span>
                <button
                  onClick={() => { setShowDomainImport(false); setDomainMsg(""); }}
                  style={{ background: "none", border: "none", cursor: "pointer", marginLeft: "auto", color: "#94a3b8", display: "flex" }}
                >
                  <FaTimes size={12} />
                </button>
              </div>
              <p style={{ fontSize: 13, color: "#64748b", margin: "0 0 10px", lineHeight: 1.5 }}>
                Paste your Domain.com.au agent profile link to auto-fill branding and details.
              </p>
              <div style={{ display: "flex", gap: 8, flexWrap: isMobile ? "wrap" : "nowrap" }}>
                <input
                  style={{ ...inputStyle, flex: 1, minWidth: 0, fontSize: 13 }}
                  value={domainUrl} onChange={e => { setDomainUrl(e.target.value); setDomainMsg(""); }}
                  placeholder="https://www.domain.com.au/real-estate-agent/your-name-123456/"
                />
                <button
                  onClick={handleDomainImport}
                  disabled={domainLoading || !domainUrl.trim()}
                  style={{ ...btnPrimary, padding: "10px 20px", fontSize: 13, opacity: domainLoading || !domainUrl.trim() ? 0.5 : 1, flexShrink: 0 }}
                >
                  {domainLoading ? <InlineSpinner size={13} /> : "Import"}
                </button>
              </div>
              {domainMsg && (
                <div style={{
                  marginTop: 8, fontSize: 13, color: domainMsg.includes("success") ? "#059669" : "#dc2626",
                  display: "flex", alignItems: "center", gap: 6,
                }}>
                  {domainMsg.includes("success") ? <FaCheckCircle size={12} /> : <FaExclamationTriangle size={12} />}
                  {domainMsg}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Branded page link */}
      {slug && (
        <div style={{
          ...card, padding: isMobile ? "16px 20px" : "20px 28px",
          background: "linear-gradient(135deg, #f0f9ff 0%, #f0fdf4 100%)",
          border: "1px solid #bae6fd",
          display: "flex", alignItems: isMobile ? "flex-start" : "center",
          gap: 14, flexDirection: isMobile ? "column" : "row",
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#0c4a6e", marginBottom: 2 }}>Your Branded Property Search</div>
            <div style={{ fontSize: 12, color: "#0369a1", wordBreak: "break-all", fontFamily: "'SF Mono', Consolas, monospace" }}>
              homeprice.site/property-search-live?agent={slug}
            </div>
          </div>
          <a
            href={`https://www.homeprice.site/property-search-live?agent=${slug}`}
            target="_blank" rel="noopener noreferrer"
            style={{ ...btnGhost, textDecoration: "none", flexShrink: 0, borderStyle: "solid", borderColor: "#93c5fd" }}
          >
            <FaExternalLinkAlt size={11} /> Preview
          </a>
        </div>
      )}
    </div>
  );

  // ===================== STEP 2: SUBURBS =====================
  const renderSuburbSearch = (ref, dropRef, query, setQuery, results, searching, dropOpen, onSearch, onSelect, placeholder) => (
    <div style={{ position: "relative" }} ref={dropRef}>
      <div style={{ position: "relative" }}>
        <FaSearch style={{ position: "absolute", left: 14, top: 14, color: "#94a3b8", fontSize: 13 }} />
        <input
          ref={ref}
          style={{ ...inputStyle, paddingLeft: 38 }}
          value={query}
          onChange={e => { setQuery(e.target.value); onSearch(e.target.value); }}
          placeholder={placeholder}
        />
        {searching && <span style={{ position: "absolute", right: 14, top: 13 }}><InlineSpinner size={14} color="#94a3b8" /></span>}
      </div>
      {dropOpen && results.length > 0 && (
        <div style={{
          position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 20,
          background: "#fff", border: "1.5px solid #e2e8f0", borderRadius: 14,
          boxShadow: "0 8px 24px rgba(0,0,0,0.08)", maxHeight: 200, overflowY: "auto",
        }}>
          {results.map((item, i) => (
            <div
              key={item.value}
              onClick={() => onSelect(item)}
              style={{
                padding: "11px 16px", cursor: "pointer", fontSize: 14, color: "#0f172a",
                borderBottom: i < results.length - 1 ? "1px solid #f8fafc" : "none",
                transition: "background 0.15s",
              }}
              onMouseEnter={e => e.currentTarget.style.background = "#f0fdfa"}
              onMouseLeave={e => e.currentTarget.style.background = "#fff"}
            >
              {item.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderStep2 = () => (
    <div style={card}>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 22, fontWeight: 700, color: "#0f172a", margin: 0, letterSpacing: "-0.01em" }}>
          Your Target Market
        </h2>
        <p style={{ fontSize: 14, color: "#64748b", margin: "6px 0 0", lineHeight: 1.6 }}>
          Set the suburbs you operate in. Your primary suburb is used for market update newsletters.
        </p>
      </div>

      {/* Primary suburb */}
      <div style={{ marginBottom: 24 }}>
        <label style={labelStyle}>Primary Suburb</label>
        {primarySuburb ? (
          <div style={{
            display: "flex", alignItems: "center", gap: 10,
            background: "linear-gradient(135deg, #f0fdfa, #ecfdf5)", border: "1.5px solid #a7f3d0",
            borderRadius: 12, padding: "12px 16px",
          }}>
            <FaMapMarkerAlt style={{ color: "#059669", flexShrink: 0 }} />
            <span style={{ flex: 1, fontSize: 15, fontWeight: 500, color: "#065f46" }}>{primarySuburb.label}</span>
            <button
              onClick={() => setPrimarySuburb(null)}
              style={{ background: "none", border: "none", cursor: "pointer", padding: 2, color: "#6b7280", display: "flex" }}
            >
              <FaTimes size={13} />
            </button>
          </div>
        ) : (
          renderSuburbSearch(primaryInputRef, primaryDropdownRef, primaryQuery, setPrimaryQuery, primaryResults, primarySearching, primaryDropdownOpen, searchPrimary, handleSelectPrimary, "Search for your primary suburb...")
        )}
      </div>

      {/* Target suburbs multi-select */}
      <div>
        <label style={labelStyle}>Target Suburbs</label>
        <p style={{ fontSize: 13, color: "#94a3b8", margin: "-2px 0 10px", lineHeight: 1.5 }}>
          Add all suburbs you want to receive market data and leads for.
        </p>
        {renderSuburbSearch(suburbInputRef, suburbDropdownRef, suburbQuery, setSuburbQuery, suburbResults, suburbSearching, suburbDropdownOpen, searchSuburbs, handleSelectSuburb, "Search to add suburbs...")}

        {targetSuburbs.length > 0 ? (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 14 }}>
            {targetSuburbs.map((s, i) => (
              <div key={s.value} style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                background: "#f0fdfa", border: "1px solid #ccfbf1",
                borderRadius: 20, padding: "7px 14px",
                fontSize: 13, color: "#134e4a", fontWeight: 500,
              }}>
                {s.label}
                <button
                  onClick={() => handleRemoveSuburb(i)}
                  style={{ background: "none", border: "none", cursor: "pointer", padding: 0, color: "#6b7280", display: "flex" }}
                >
                  <FaTimes size={10} />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ textAlign: "center", padding: "28px 0", color: "#cbd5e1", fontSize: 14 }}>
            Search above to add your target suburbs
          </div>
        )}
      </div>
    </div>
  );

  // ===================== STEP 3: CONTACTS =====================
  const renderStep3 = () => (
    <div>
      <div style={card}>
        <div style={{ marginBottom: 20 }}>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: "#0f172a", margin: 0, letterSpacing: "-0.01em" }}>
            Upload Contacts
          </h2>
          <p style={{ fontSize: 14, color: "#64748b", margin: "6px 0 0", lineHeight: 1.6 }}>
            Upload a spreadsheet of your contacts and our AI will automatically clean and organise the data for you. <strong>The format is flexible</strong> — your file doesn't need to match an exact template.
          </p>
          <p style={{ fontSize: 13, color: "#94a3b8", margin: "6px 0 0", lineHeight: 1.6 }}>
            We'll look for these fields (columns can be named however you like):
          </p>
        </div>

        <div style={{
          background: "#fafbfc", border: "1.5px solid #e2e8f0", borderRadius: 14,
          padding: "14px 18px", marginBottom: 16, fontSize: 13, color: "#334155", lineHeight: 1.7,
        }}>
          <strong>Email</strong>, <strong>Mobile</strong>, <strong>First Name</strong>, <strong>Last Name</strong><br />
          <strong>Address</strong> (unit number, street number, street name, suburb, postcode, state)
          <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 4 }}>
            The address can be in a single column or split across multiple columns — our AI handles both. If no street address is available, just provide suburb, postcode and state.
          </div>
        </div>

        <div style={{
          background: "#fffbeb", border: "1.5px solid #fef3c7", borderRadius: 14,
          padding: "12px 16px", marginBottom: 20, fontSize: 13, color: "#92400e", lineHeight: 1.5,
          display: "flex", alignItems: "flex-start", gap: 8,
        }}>
          <FaExclamationTriangle size={13} style={{ flexShrink: 0, marginTop: 2 }} />
          <span>It typically takes <strong>24 hours</strong> to process and address-match your contacts to our database.</span>
        </div>

        {/* Dropzone */}
        <div
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          style={{
            border: `2px dashed ${dragOver ? "#0f766e" : "#d1d5db"}`,
            borderRadius: 14, padding: "32px 16px", textAlign: "center",
            cursor: "pointer", marginBottom: 16, transition: "all 0.2s",
            background: dragOver ? "#f0fdfa" : "#fafbfc",
          }}
        >
          <input
            ref={fileInputRef} type="file" accept=".csv,.xlsx,.xls" style={{ display: "none" }}
            onChange={e => { if (e.target.files[0]) handleFile(e.target.files[0]); }}
          />
          {file ? (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
              <FaFileAlt size={16} color="#0f766e" />
              <span style={{ fontSize: 14, fontWeight: 500, color: "#0f172a" }}>{file.name}</span>
              <span style={{ fontSize: 12, color: "#94a3b8" }}>({(file.size / 1024).toFixed(0)} KB)</span>
            </div>
          ) : (
            <>
              <FaCloudUploadAlt size={28} color="#cbd5e1" style={{ marginBottom: 8 }} />
              <div style={{ fontSize: 14, color: "#64748b" }}>
                Drag & drop or <span style={{ color: "#0f766e", fontWeight: 600 }}>browse</span>
              </div>
              <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 4 }}>CSV or Excel files</div>
            </>
          )}
        </div>

        {uploading && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
              <span style={{ fontSize: 12, color: "#64748b" }}>Uploading...</span>
              <span style={{ fontSize: 12, color: "#64748b" }}>{Math.round(progress)}%</span>
            </div>
            <div style={{ width: "100%", height: 4, background: "#e2e8f0", borderRadius: 2, overflow: "hidden" }}>
              <div style={{
                width: `${progress}%`, height: "100%",
                background: "linear-gradient(90deg, #0f766e, #10b981)",
                borderRadius: 2, transition: "width 0.1s linear",
              }} />
            </div>
          </div>
        )}

        {uploadResult && (
          <div style={{
            display: "flex", alignItems: "flex-start", gap: 8,
            padding: "12px 16px", borderRadius: 14, marginBottom: 16,
            background: uploadResult.success ? "#f0fdf4" : "#fef2f2",
            border: uploadResult.success ? "1.5px solid #bbf7d0" : "1.5px solid #fecaca",
            fontSize: 13, color: uploadResult.success ? "#166534" : "#991b1b", lineHeight: 1.5,
          }}>
            {uploadResult.success ? <FaCheckCircle size={13} style={{ flexShrink: 0, marginTop: 2 }} /> : <FaExclamationTriangle size={13} style={{ flexShrink: 0, marginTop: 2 }} />}
            <span>{uploadResult.message}</span>
          </div>
        )}

        <button
          onClick={handleUploadContacts}
          disabled={!file || uploading}
          style={{ ...btnPrimary, width: "100%", justifyContent: "center", opacity: !file || uploading ? 0.4 : 1, cursor: !file || uploading ? "default" : "pointer" }}
        >
          {uploading ? "Uploading..." : "Upload Contacts"}
        </button>
      </div>

      {/* CRM card */}
      <div style={{
        ...card,
        background: "linear-gradient(135deg, #faf5ff 0%, #f5f3ff 100%)",
        border: "1px solid #e9d5ff",
      }}>
        <h3 style={{ fontSize: 17, fontWeight: 700, color: "#6d28d9", margin: "0 0 4px" }}>
          Or connect your CRM
        </h3>
        <p style={{ fontSize: 13, color: "#64748b", margin: "0 0 14px", lineHeight: 1.5 }}>
          Connect your CRM and our AI agent will securely access your contacts — browsing your CRM like a human would.
        </p>
        {crmSent ? (
          <div style={{
            display: "flex", alignItems: "center", gap: 8, padding: "12px 16px", borderRadius: 12,
            background: "#f0fdf4", border: "1.5px solid #bbf7d0",
            fontSize: 14, color: "#166534", fontWeight: 500,
          }}>
            <FaCheckCircle /> CRM connected successfully!
          </div>
        ) : (
          <button
            onClick={() => setShowCRMModal(true)}
            style={{
              ...btnPrimary, background: "linear-gradient(135deg, #7c3aed, #a855f7)",
              boxShadow: "0 2px 8px rgba(124,58,237,0.25)",
              padding: "12px 20px", fontSize: 14, width: "100%",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            }}
          >
            <FaPlug size={13} /> Connect CRM
          </button>
        )}
      </div>
      <ConnectCRMModal
        show={showCRMModal}
        onClose={() => setShowCRMModal(false)}
        onSuccess={() => { setCrmSent(true); }}
        fullScreen
      />
    </div>
  );

  // ===================== MAIN RENDER =====================
  return (
    <div style={{
      position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
      zIndex: 9999,
      background: "linear-gradient(160deg, #f8fafc 0%, #f0fdf4 50%, #f0f9ff 100%)",
      display: "flex", flexDirection: "column",
      overflow: "hidden",
    }}>
      <style>{WIZARD_CSS}</style>

      {/* Top bar */}
      <div style={{
        background: "rgba(255,255,255,0.85)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)",
        borderBottom: "1px solid rgba(0,0,0,0.06)",
        padding: isMobile ? "10px 16px" : "12px 32px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        flexShrink: 0,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <GradientHouseIcon width="28" height="28" />
          <span style={{ fontSize: 16, fontWeight: 700, color: "#0f172a" }}>
            Welcome to <VendorleadsGradientText style={{ fontSize: 16 }}>Vendorleads</VendorleadsGradientText>
          </span>
        </div>
        {step === STEPS.length - 1 && (
          <button
            onClick={handleFinish}
            style={{
              background: "none", border: "none", cursor: "pointer",
              fontSize: 13, color: "#94a3b8", display: "flex", alignItems: "center", gap: 4,
            }}
          >
            Skip <FaTimes size={13} />
          </button>
        )}
      </div>

      {/* Content */}
      <div style={{
        flex: 1, overflowY: "auto",
        padding: isMobile ? "28px 16px 120px" : "36px 32px 120px",
        display: "flex", flexDirection: "column", alignItems: "center",
      }}>
        <div style={{ width: "100%", maxWidth: 600 }}>
          {renderStepIndicator()}
          {step === 0 && renderStep0()}
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
        </div>
      </div>

      {/* Bottom bar */}
      <div style={{
        background: "rgba(255,255,255,0.85)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)",
        borderTop: "1px solid rgba(0,0,0,0.06)",
        padding: isMobile ? "12px 16px" : "14px 32px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        flexShrink: 0,
      }}>
        <div>
          {step > 0 && (
            <button onClick={() => setStep(step - 1)} style={btnSecondary}>
              <FaArrowLeft size={12} /> Back
            </button>
          )}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {step < STEPS.length - 1 ? (
            <button
              onClick={() => saveStep(step + 1)}
              disabled={saving}
              style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }}
            >
              {saving ? <InlineSpinner size={14} /> : <>Continue <FaArrowRight size={12} /></>}
            </button>
          ) : (
            <button onClick={handleFinish} style={btnPrimary}>
              Finish Setup <FaCheckCircle size={14} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
